import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
});

function ResetPassword() {
  const { token: tokenParam, error: errorParam } = useSearch({
    from: "/reset-password",
    select: (s) => ({ token: (s as any).token as string | undefined, error: (s as any).error as string | undefined }),
  });

  const hasInvalidError = useMemo(
    () => typeof errorParam === "string" && errorParam.toLowerCase().includes("invalid"),
    [errorParam]
  );

  const [token] = useState<string | undefined>(tokenParam);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const validate = () => {
    let ok = true;
    const p = password.trim();
    const c = confirm.trim();
    if (p.length < 8) {
      setPasswordError("password must be at least 8 characters");
      ok = false;
    }
    if (c !== p) {
      setConfirmError("passwords do not match");
      ok = false;
    }
    return ok;
  };

  const handleReset = async () => {
    setPasswordError(null);
    setConfirmError(null);
    if (!token) {
      toast.error("missing or invalid reset token");
      return;
    }
    if (!validate()) return;

    await authClient.resetPassword(
      { newPassword: password.trim(), token },
      {
        onRequest: () => setLoading(true),
        onResponse: () => setLoading(false),
        onSuccess: () => {
          toast.success("your password has been reset");
          window.location.href = "/sign-in";
        },
      }
    );
  };

  const showForm = Boolean(token) && !hasInvalidError;

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <div className="flex w-full flex-col items-center gap-6">
        <img src="/header-logo.png" alt="clove.moe" className="h-12 w-auto align-middle" />
        <Card className="z-50 w-full max-w-md rounded-md shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl text-[#353535]">reset password</CardTitle>
            <CardDescription className="text-xs md:text-sm text-accent">
              {showForm
                ? "choose a new password for your account"
                : "this link is invalid or expired, request a new one"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showForm ? (
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="password" className="text-[#353535]">new password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="new password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPassword(v);
                        if (passwordError && v.trim().length >= 8) setPasswordError(null);
                      }}
                      aria-invalid={Boolean(passwordError) || undefined}
                      aria-describedby={passwordError ? "password-error" : undefined}
                      className={`${passwordError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="password-error" className="text-xs text-red-600 mt-1">{passwordError}</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="confirm" className="text-[#353535]">confirm password</Label>
                  <div className="relative">
                    <Input
                      id="confirm"
                      type={showConfirm ? "text" : "password"}
                      placeholder="confirm password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => {
                        const v = e.target.value;
                        setConfirm(v);
                        if (confirmError && v.trim() === password.trim()) setConfirmError(null);
                      }}
                      aria-invalid={Boolean(confirmError) || undefined}
                      aria-describedby={confirmError ? "confirm-error" : undefined}
                      className={`${confirmError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {confirmError && (
                    <p id="confirm-error" className="text-xs text-red-600 mt-1">{confirmError}</p>
                  )}
                </div>

                <Button
                  type="button"
                  className="w-full bg-accent text-white hover:opacity-90"
                  disabled={loading}
                  onClick={handleReset}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "reset password"}
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                <a href="/forgot-password" className="text-sm text-accent underline">request a new reset link</a>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <div className="flex justify-center w-full border-t pt-4">
              <div className="flex items-center gap-2">
                <a href="/sign-in" className="inline-block text-sm text-accent underline">back to sign in</a>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
